import React, { useState, useEffect } from "react";
import { firstCompany } from "../../../helper/attribute";
import { 
  Card, Form, Input, Button, message, Table, Modal, 
  Space, Typography, Popconfirm, Empty, Tabs,
  Checkbox
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, FileTextOutlined } from "@ant-design/icons";
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase/setup";
import FileInput from "../../../components/input/datafile";
import RichTextEditor from "../../../components/RTE";
import AntdElementsForm from "../../../components/AntdElements/FormGen";

// Configuration
const CONFIG = {
  STORAGE_PATH: "userAppsData",
  COLLECTION_NAME: "UserAppsData",
  FORM_DOC_PREFIX: "forms",
  ALLOWED_IMAGE_TYPES: ["image/jpeg", "image/png"],
  MAX_IMAGE_SIZE: 5 * 1024 * 1024, // 5MB
};

const { Title } = Typography;
const { TabPane } = Tabs;

// Form-compatible RichTextEditor wrapper
const FormRichTextEditor = ({ value = '', onChange, placeholder }) => {
  const handleChange = (newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <RichTextEditor 
      value={value} 
      onChange={handleChange} 
      placeholder={placeholder} 
    />
  );
};

export default function ExternalForms() {
  const [companyId] = useState(firstCompany()?.id);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [form] = Form.useForm();
  
  // Questions modal state
  const [questionsModalVisible, setQuestionsModalVisible] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [questions, setQuestions] = useState([]);
  
  // Form submission state
  const [submissionFormId, setSubmissionFormId] = useState(null);
  const [submissionModalVisible, setSubmissionModalVisible] = useState(false);
  const [formData, setFormData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Responses state
  const [responsesFormId, setResponsesFormId] = useState(null);
  const [responsesModalVisible, setResponsesModalVisible] = useState(false);
  const [formResponses, setFormResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  
  // Key for forcing re-render of components
  const [questionsKey, setQuestionsKey] = useState(0);

  // Fetch forms on component mount
  useEffect(() => {
    if (companyId) {
      fetchForms();
    }
  }, [companyId]);

  // Fetch forms from Firestore
  const fetchForms = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, CONFIG.COLLECTION_NAME),
        where("type", "==", CONFIG.FORM_DOC_PREFIX),
        where("companyId", "==", companyId)
      );
      const querySnapshot = await getDocs(q);
      const formsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        questions: doc.data().questions || []
      }));
      setForms(formsData);
    } catch (error) {
      console.error("Error fetching forms:", error);
      message.error("Failed to fetch forms");
    } finally {
      setLoading(false);
    }
  };

  // Fetch form responses
  const fetchFormResponses = async (formId) => {
    try {
      setLoadingResponses(true);
      const q = query(
        collection(db, CONFIG.COLLECTION_NAME),
        where("type", "==", "form_response"),
        where("formId", "==", formId)
      );
      const querySnapshot = await getDocs(q);
      const responsesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("Fetched responses:", responsesData);
      setFormResponses(responsesData);
    } catch (error) {
      console.error("Error fetching form responses:", error);
      message.error("Failed to fetch form responses");
    } finally {
      setLoadingResponses(false);
    }
  };

  // Handle form submission (create/update)
  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);
      let imageUrl = coverImageUrl;

      // Upload image if there's a new file
      if (coverImageFile) {
        const storageRef = ref(storage, `${CONFIG.STORAGE_PATH}/${companyId}/${Date.now()}_${coverImageFile.name}`);
        await uploadBytes(storageRef, coverImageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const formData = {
        ...values,
        coverImage: imageUrl,
        companyId,
        type: CONFIG.FORM_DOC_PREFIX,
        updatedAt: new Date().toISOString(),
      };

      if (editingForm) {
        // Update existing form
        await updateDoc(doc(db, CONFIG.COLLECTION_NAME, editingForm.id), formData);
        message.success("Form updated successfully");
      } else {
        // Create new form
        formData.createdAt = new Date().toISOString();
        await addDoc(collection(db, CONFIG.COLLECTION_NAME), formData);
        message.success("Form created successfully");
      }

      resetFormState();
      fetchForms();
    } catch (error) {
      console.error("Error saving form:", error);
      message.error("Failed to save form");
    } finally {
      setLoading(false);
    }
  };

  // Delete form
  const handleDeleteForm = async (formId, coverImagePath) => {
    try {
      setLoading(true);
      
      // Delete form document
      await deleteDoc(doc(db, CONFIG.COLLECTION_NAME, formId));
      
      // Delete cover image if exists
      if (coverImagePath) {
        try {
          const imageRef = ref(storage, coverImagePath);
          await deleteObject(imageRef);
        } catch (err) {
          console.error("Error deleting image:", err);
        }
      }
      
      message.success("Form deleted successfully");
      fetchForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      message.error("Failed to delete form");
    } finally {
      setLoading(false);
    }
  };

  // Reset form state
  const resetFormState = () => {
    setModalVisible(false);
    setEditingForm(null);
    setCoverImageUrl("");
    setCoverImageFile(null);
    form.resetFields();
  };

  // Edit form
  const handleEditForm = (record) => {
    setEditingForm(record);
    setCoverImageUrl(record.coverImage || "");
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      instructions: record.instructions,
      carbon_calculator: record.carbon_calculator || false,
    });
    setModalVisible(true);
  };

  // Handle image upload
  const handleImageChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    
    if (info.file.originFileObj) {
      setCoverImageFile(info.file.originFileObj);
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setCoverImageUrl(reader.result);
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  // Handle manage questions
  const handleManageQuestions = async (formId) => {
    setCurrentFormId(formId);
    const currentForm = forms.find(form => form.id === formId);
    setQuestions(currentForm?.questions || []);
    setQuestionsModalVisible(true);
    setQuestionsKey(prevKey => prevKey + 1);
  };

  // Save questions to form
  const handleSaveQuestions = async (updatedQuestions) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, CONFIG.COLLECTION_NAME, currentFormId), {
        questions: updatedQuestions,
        updatedAt: new Date().toISOString()
      });
      message.success("Questions saved successfully");
      setQuestionsModalVisible(false);
      fetchForms();
    } catch (error) {
      console.error("Error saving questions:", error);
      message.error("Failed to save questions");
    } finally {
      setLoading(false);
    }
  };

  // Handle form response submission
  const handleSubmitResponse = async (formDataToSubmit) => {
    try {
      setSubmitting(true);
      const currentForm = forms.find(form => form.id === submissionFormId);
      
      // Check if formDataToSubmit is empty
      if (!formDataToSubmit || Object.keys(formDataToSubmit).length === 0) {
        message.warning("Please fill out the form before submitting");
        setSubmitting(false);
        return;
      }
      
      console.log("Form data before submission:", formDataToSubmit);
      
      // Format the form data for storage
      let formattedData = [];
      
      if (typeof formDataToSubmit === 'object' && !Array.isArray(formDataToSubmit)) {
        // If formDataToSubmit is a single object with field IDs as keys
        formattedData = Object.keys(formDataToSubmit).map(fieldId => {
          const field = currentForm?.questions.find(q => q.id === fieldId);
          return {
            fieldId,
            label: field?.label || "Unknown question",
            value: formDataToSubmit[fieldId]
          };
        });
      }
      
      // Save response to Firestore
      await addDoc(collection(db, CONFIG.COLLECTION_NAME), {
        type: "form_response",
        formId: submissionFormId,
        formTitle: currentForm?.title,
        companyId,
        data: formattedData,
        createdAt: new Date().toISOString()
      });
      
      message.success("Form response submitted successfully");
      setSubmissionModalVisible(false);
      setFormData({});
    } catch (error) {
      console.error("Error submitting form response:", error);
      message.error("Failed to submit form response");
    } finally {
      setSubmitting(false);
    }
  };

  // Generate dynamic columns for responses table
  const generateResponseColumns = (responses) => {
    if (!responses || responses.length === 0) return [];
    
    // Get all unique field labels across all responses
    const allFields = new Set();
    responses.forEach(response => {
      if (response.data && Array.isArray(response.data)) {
        response.data.forEach(field => {
          if (field.label) allFields.add(field.label);
        });
      }
    });
    
    // Create base columns
    const columns = [
      {
        title: 'Submission Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (text) => new Date(text).toLocaleString(),
        width: 180,
      }
    ];
    
    // Add a column for each field
    Array.from(allFields).forEach(fieldLabel => {
      columns.push({
        title: fieldLabel,
        dataIndex: 'data',
        key: fieldLabel,
        render: (data) => {
          const field = data?.find(item => item.label === fieldLabel);
          if (!field) return '-';
          
          const value = field.value;
          if (Array.isArray(value)) {
            return value.join(', ');
          } else if (value === null || value === undefined) {
            return '-';
          } else {
            return String(value);
          }
        }
      });
    });
    
    return columns;
  };

  // Form columns for table
  const formColumns = [
    {
      title: 'Cover Image',
      dataIndex: 'coverImage',
      key: 'coverImage',
      width: 80,
      render: (text) => text ? <img src={text} alt="Cover" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : '-',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Questions',
      dataIndex: 'questions',
      key: 'questions',
      render: (questions) => questions?.length || 0,
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEditForm(record)}
            size="small"
          >
            Edit
          </Button>
          <Button 
            onClick={() => handleManageQuestions(record.id)}
            size="small"
          >
            Questions
          </Button>
          <Button 
            onClick={() => {
              setResponsesFormId(record.id);
              fetchFormResponses(record.id);
              setResponsesModalVisible(true);
            }}
            size="small"
          >
            Responses
          </Button>
          <Button 
            type="primary"
            onClick={() => {
              setSubmissionFormId(record.id);
              setSubmissionModalVisible(true);
              setFormData({});
            }}
            size="small"
          >
            Submit
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteForm(record.id, record.coverImage)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4}>External Forms</Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setModalVisible(true)}
          >
            Create Form
          </Button>
        </div>
        
        <Table 
          columns={formColumns} 
          dataSource={forms} 
          rowKey="id" 
          loading={loading}
          expandable={{
            expandedRowRender: record => (
              <div>
                <p><strong>Description:</strong> <div dangerouslySetInnerHTML={{ __html: record.description }} /></p>
                <p><strong>Instructions:</strong> <div dangerouslySetInnerHTML={{ __html: record.instructions }} /></p>
              </div>
            ),
          }}
        />
      </Card>

      {/* Form Modal */}
      <Modal
        title={editingForm ? "Edit Form" : "Create Form"}
        open={modalVisible}
        onCancel={resetFormState}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          <Form.Item
            label="Cover Image"
          >
            <FileInput 
              file={coverImageFile}
              setFile={setCoverImageFile}
              fileSize={CONFIG.MAX_IMAGE_SIZE}
              accept="image/jpeg,image/png"
              extraWord="cover image"
            />
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Form Title" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <FormRichTextEditor placeholder="Form Description" />
          </Form.Item>

          <Form.Item
            name="carbon_calculator"
            valuePropName="checked"
            label="Add Carbon Calculator"
          >
            <Checkbox>Yes</Checkbox>
          </Form.Item>

          <Form.Item
            label="Instructions"
            name="instructions"
            rules={[{ required: true, message: 'Please enter instructions' }]}
          >
            <FormRichTextEditor placeholder="Instructions for filling out the form" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingForm ? "Update Form" : "Create Form"}
              </Button>
              <Button onClick={resetFormState}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Questions Modal */}
      <Modal
        title="Manage Form Questions"
        open={questionsModalVisible}
        onCancel={() => setQuestionsModalVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {currentFormId && (
          <div>
            <Title level={5}>Questions for: {forms.find(f => f.id === currentFormId)?.title}</Title>
            <AntdElementsForm 
              key={`questions-${currentFormId}-${questionsKey}`}
              initialFields={questions} 
              onFieldsChange={setQuestions} 
              displayMode="editor"
            />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button 
                type="primary" 
                onClick={() => handleSaveQuestions(questions)}
                loading={loading}
              >
                Save Questions
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Form Submission Modal */}
      <Modal
        title="Submit Form"
        open={submissionModalVisible}
        onCancel={() => setSubmissionModalVisible(false)}
        footer={null}
        width={800}
      >
        {submissionFormId && (
          <div>
            <Title level={5}>{forms.find(f => f.id === submissionFormId)?.title}</Title>
            <div dangerouslySetInnerHTML={{ __html: forms.find(f => f.id === submissionFormId)?.instructions }} />
            <AntdElementsForm 
              submitting={submitting}
              key={`form-${submissionFormId}`}
              initialFields={forms.find(f => f.id === submissionFormId)?.questions || []}
              displayMode="preview"
              onFormSubmit={(values) => {
                console.log("Form submitted with values:", values);
                setFormData(values);
                handleSubmitResponse(values);
              }}
            />
          </div>
        )}
      </Modal>
      
      {/* Responses Modal */}
      <Modal
        title="Form Responses"
        open={responsesModalVisible}
        onCancel={() => setResponsesModalVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {responsesFormId && (
          <div>
            <Title level={5}>Responses for: {forms.find(f => f.id === responsesFormId)?.title}</Title>
            {formResponses.length > 0 ? (
              <Table 
                columns={generateResponseColumns(formResponses)} 
                dataSource={formResponses} 
                rowKey="id"
                loading={loadingResponses}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 10 }}
              />
            ) : (
              <Empty description="No responses yet" />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

